type CssUnitValue = {
  value: number;
  unit: string;
};

const defaultProperties: Partial<Record<keyof CSSStyleDeclaration, { defaultUnit: string; defaultValue?: number }>> =
  {};

defaultProperties.lineHeight =
  defaultProperties.opacity =
  defaultProperties.scale =
    {
      defaultUnit: "",
      defaultValue: 1,
    };

defaultProperties.rotate = {
  defaultUnit: "deg",
};

export const parseCssUnitValue = (value: string, property?: keyof CSSStyleDeclaration) => {
  const parts: CssUnitValue = { value: 0, unit: "" };

  if (!isNaN(Number(value))) {
    parts.value = Number(value);
    parts.unit = property ? getDefaultUnit(property) : "";
  } else {
    const matches = value
      .toString()
      .trim()
      .match(/^(-?[\d+.-]+)([a-z]+|%)$/i);

    if (matches !== null) {
      parts.value = Number(matches[1]);
      parts.unit = matches[2];
    } else {
      parts.unit = value;
      parts.value = property ? getDefaultValue(property) : 0;
    }
  }

  return parts;
};

const getDefaultValue = (property: keyof CSSStyleDeclaration) => {
  const defaultProperty = defaultProperties[property];
  return defaultProperty && defaultProperty.defaultValue ? defaultProperty.defaultValue : 0;
};

const getDefaultUnit = (property: keyof CSSStyleDeclaration) => {
  const defaultProperty = defaultProperties[property];
  return defaultProperty && defaultProperty.defaultUnit ? defaultProperty.defaultUnit : "px";
};

export interface IncrementCssValueParams {
  value: string;
  delta: number;
  fallbackUnit?: string;
}

/**
 * Increments or decrements a CSS value string by a given delta.
 * Parses the numeric portion, applies the delta, and reattaches the unit.
 * Returns undefined for non-numeric values (e.g. "auto", "inherit").
 * Empty or unitless values default to the provided fallback unit.
 */
export const incrementCssValue = ({
  value,
  delta,
  fallbackUnit = "px",
}: IncrementCssValueParams): string | undefined => {
  const parsed = parseCssUnitValue(value || "0");
  if (value && parsed.unit === value) return undefined;
  const resolvedUnit = parsed.unit || fallbackUnit;
  return `${Math.round((parsed.value + delta) * 1000) / 1000}${resolvedUnit}`;
};

const roundToOneDecimalPlace = (value: number): number => Math.round(value * 10) / 10;

/**
 * Formats a dragged `transform-origin` into a CSS value, preserving the unit of
 * each axis from the previous authored value (px, %, em, rem, vw, …). The new
 * value is the previous one scaled in fraction space — moving the origin from
 * `startFraction` to `fraction` multiplies the authored number by
 * `fraction / startFraction`, which cancels out the unit's pixel basis so any
 * length unit is preserved without knowing it. Axes that can't be scaled
 * (keyword/unitless/unset values, or a zero starting fraction) fall back to a
 * percentage. Rounded to one decimal place.
 */
export const formatDraggedTransformOrigin = (
  fractions: { ox: number; oy: number },
  startFractions: { ox: number; oy: number },
  previousValue: string | undefined,
): string => {
  const [xToken = "", yToken = ""] = (previousValue ?? "").trim().split(/\s+/);
  const axis = (fraction: number, startFraction: number, token: string) => {
    const { value, unit } = parseCssUnitValue(token);
    // unit === token means parsing fell back to a keyword (e.g. "center"); "" means unitless.
    const hasScalableUnit = unit !== "" && unit !== token;
    if (!hasScalableUnit || startFraction === 0) return `${roundToOneDecimalPlace(fraction * 100)}%`;
    return `${roundToOneDecimalPlace((fraction / startFraction) * value)}${unit}`;
  };
  return `${axis(fractions.ox, startFractions.ox, xToken)} ${axis(fractions.oy, startFractions.oy, yToken)}`;
};
